import { Injectable, inject, signal } from '@angular/core';
import { PlanPrice } from './checkout.service';
import { CheckoutProviderService } from './checkout-provider.service';
import { StorageService } from './storage.service';
import { formatAmount, monthsFreeOf } from './pricing.util';

/**
 * Locale de mise en forme, déduite de la langue de l'app.
 *
 * `en-IE` et non `en-US` : c'est une locale anglophone de la zone euro, qui
 * écrit « €3.99 » là où `en-US` écrirait « €3.99 » aussi mais avec des règles
 * de séparateurs américaines sur les grands nombres.
 */
const LOCALES: Record<string, string> = {
  FR: 'fr-BE',
  NL: 'nl-BE',
  DE: 'de-DE',
  IT: 'it-IT',
  ES: 'es-ES',
  EN: 'en-IE',
};

/**
 * LA source des montants affichés.
 *
 * Règle absolue : aucun prix n'est écrit dans l'app, ni dans un libellé i18n ni
 * dans un composant. Les libellés ne portent plus que le gabarit
 * (« {{price}}/mois ») et le montant vient du fournisseur de paiement : Stripe
 * sur le web, le magasin sur mobile.
 *
 * Pourquoi c'est indispensable : le prix d'un achat intégré varie par pays
 * (3,99 € en Belgique, un autre palier en France, une autre devise ailleurs).
 * Afficher un montant que le magasin ne facture pas trompe l'utilisateur, et
 * c'est un motif de refus explicite chez Apple.
 *
 * Corollaire : « ≈ 2 mois offerts » et « soit 3,33 €/mois » sont CALCULÉS. Avec
 * 5 €/mois et 39,99 €/an, l'économie n'est plus de 2 mois mais de 4 — écrite en
 * dur, la mention serait fausse dans ce pays.
 */
@Injectable({ providedIn: 'root' })
export class PricingService {
  // La fabrique tranche à l'exécution : achat intégré dans l'app native, Stripe
  // partout ailleurs. Ce service ignore lequel a répondu.
  private readonly checkout = inject(CheckoutProviderService);
  private readonly storage = inject(StorageService);

  /** Prix rapportés par le fournisseur. Vide tant que rien n'est chargé. */
  readonly prices = signal<PlanPrice[]>([]);

  /** Une réponse est arrivée (même vide) : « aucun prix » devient une info. */
  readonly loaded = signal(false);

  /** Chargement en cours, pour ne pas le lancer deux fois. */
  private inFlight?: Promise<void>;

  /**
   * Charge les prix une seule fois. Un échec laisse la liste vide : mieux vaut
   * une formule sans montant qu'un montant inventé.
   */
  async load(): Promise<void> {
    if (this.prices().length > 0) return;
    this.inFlight ??= this.checkout
      .getPrices()
      .then((prices) => this.prices.set(prices))
      .catch(() => this.prices.set([]))
      .finally(() => {
        this.inFlight = undefined;
        this.loaded.set(true);
      });
    return this.inFlight;
  }

  /**
   * Montant à afficher, ou un tiret. Jamais un prix approché.
   *
   * Quand le magasin fournit sa propre chaîne (`priceString`), c'est ELLE qui
   * gagne : il l'a mise en forme selon les conventions du pays du compte, et la
   * recalculer rouvrirait l'écart entre le prix affiché et le prix facturé.
   */
  label(plan: string): string {
    const price = this.of(plan);
    if (!price) return '—';
    return price.priceString ?? this.format(price.amount, price.currency);
  }

  /**
   * Économie de la formule annuelle, en mois. `null` quand elle n'est pas
   * calculable — le badge disparaît alors, plutôt que d'annoncer un chiffre faux.
   */
  monthsFree(): number | null {
    return monthsFreeOf(this.of('MONTHLY'), this.of('YEARLY'));
  }

  /** Équivalent mensuel de la formule annuelle, formaté. */
  monthlyEquivalent(): string | null {
    const yearly = this.of('YEARLY');
    return yearly ? this.format(yearly.amount / 12, yearly.currency) : null;
  }

  private of(plan: string): PlanPrice | undefined {
    return this.prices().find((price) => price.plan === plan);
  }

  private format(amount: number, currency: string): string {
    const lang = (this.storage.getCurrentLanguage() ?? 'FR').toUpperCase();
    return formatAmount(amount, currency, LOCALES[lang] ?? 'fr-BE');
  }
}
